import gsap, { ScrollTrigger, SplitText } from "@lib/gsap";
import { handleEditor } from "@/webflow/detect-editor";

const TEXT_SELECTOR = "h1,h2,h3,h4,h5,h6,p,li,blockquote";
const TEXT_TAGS = new Set(["H1", "H2", "H3", "H4", "H5", "H6", "P", "LI", "BLOCKQUOTE"]);

type AnyTween = gsap.core.Timeline | gsap.core.Tween;

type Store = {
  splitInstances: Array<{ revert: () => void }>;
  timelines: AnyTween[];
};

// ─────────────────────────────────────────────────────────────
// AVAILABLE EFFECTS — use as data-text-effect="<value>"
//
// split            Line mask reveal (default — lines slide up)
// split-words      Word mask reveal (words slide up)
// sequence-wrap    Container for sequence-item children (scrub)
// sequence-item    Child of sequence-wrap (chars appear/disappear)
// ─────────────────────────────────────────────────────────────

function isInView(el: HTMLElement, threshold = 0.8): boolean {
  return el.getBoundingClientRect().top < window.innerHeight * threshold;
}

function getEffectValue(element: HTMLElement) {
  return element.dataset.textEffect?.trim() || "";
}

function resolveTextTargets(element: HTMLElement) {
  const richTextRoot = element.querySelector<HTMLElement>(".w-richtext");
  const scope = richTextRoot || element;
  const targets = Array.from(scope.querySelectorAll<HTMLElement>(TEXT_SELECTOR));

  if (targets.length > 0) return targets;
  if (TEXT_TAGS.has(element.tagName)) return [element];
  return [element];
}

function showAllTextEffects() {
  const effectElements = Array.from(document.querySelectorAll<HTMLElement>("[data-text-effect]"));

  effectElements.forEach((element) => {
    gsap.set(element, { clearProps: "all", autoAlpha: 1, visibility: "visible" });
    resolveTextTargets(element).forEach((target) => {
      gsap.set(target, { clearProps: "all", autoAlpha: 1, visibility: "visible" });
    });
  });
}

// ─── SEQUENCE EFFECT ─────────────────────────────────────────

function initTextSequenceEffect(store: Store) {
  const wraps = Array.from(document.querySelectorAll<HTMLElement>("[data-text-effect]")).filter(
    (element) => getEffectValue(element) === "sequence-wrap"
  );

  wraps.forEach((wrap) => {
    gsap.set(wrap, { autoAlpha: 1, visibility: "visible" });

    const items = Array.from(wrap.querySelectorAll<HTMLElement>("[data-text-effect]")).filter(
      (item) => getEffectValue(item) === "sequence-item"
    );
    if (!items.length) return;

    const masterTimeline = gsap.timeline(
      isInView(wrap, 1)
        ? {}
        : {
            scrollTrigger: {
              trigger: wrap,
              start: "top 40%",
              end: "bottom bottom",
              scrub: true,
            },
          }
    );

    store.timelines.push(masterTimeline);

    items.forEach((item, index) => {
      const [target] = resolveTextTargets(item);
      const text = target?.textContent?.trim() || "";
      if (!text) return;

      target.setAttribute("aria-label", text);
      gsap.set(target, { visibility: "visible" });

      const split = new SplitText(target, { type: "words,chars" });
      store.splitInstances.push(split);
      split.words.forEach((word) => word.setAttribute("aria-hidden", "true"));

      const itemTimeline = gsap.timeline();
      itemTimeline.from(split.chars, {
        autoAlpha: 0,
        stagger: { amount: 0.7, from: "start" },
        duration: 0.7,
      });

      if (index < items.length - 1) {
        itemTimeline.to(split.chars, {
          autoAlpha: 0,
          stagger: { amount: 0.7, from: "end" },
          duration: 0.7,
        });
      }

      if (index === 0) {
        masterTimeline.add(itemTimeline);
      } else {
        masterTimeline.add(itemTimeline, "-=0.15");
      }
    });
  });
}

// ─── SPLIT REVEAL EFFECT ────────────────────────────────────

function initSplitRevealEffect(store: Store) {
  const elements = Array.from(document.querySelectorAll<HTMLElement>("[data-text-effect]")).filter(
    (element) => {
      const v = getEffectValue(element);
      return v === "split" || v === "split-words";
    }
  );

  elements.forEach((element) => {
    if (element.closest('[data-text-effect="sequence-wrap"]')) return;

    const isWords = getEffectValue(element) === "split-words";

    gsap.set(element, { autoAlpha: 1, visibility: "visible" });

    resolveTextTargets(element).forEach((target) => {
      gsap.set(target, { autoAlpha: 1 });

      const split = SplitText.create(target, {
        type: isWords ? "words" : "lines",
        mask: isWords ? "words" : "lines",
        autoSplit: true,
        ...(isWords ? { wordsClass: "word" } : { linesClass: "line" }),
      });

      store.splitInstances.push(split);

      const units = isWords ? split.words : split.lines;

      gsap.set(units, { y: "110%" });

      const tween = gsap.to(units, {
        y: "0%",
        duration: 1,
        ease: "expo.out",
        stagger: 0.1,
        paused: true,
      });

      store.timelines.push(tween);

      if (isInView(element)) {
        tween.play();
      } else {
        ScrollTrigger.create({
          trigger: element,
          start: "clamp(top 80%)",
          animation: tween,
          once: true,
        });
      }
    });
  });
}

// ─── INIT ───────────────────────────────────────────────────

export function initTextEffects() {
  gsap.registerPlugin(ScrollTrigger, SplitText);

  const store: Store = {
    splitInstances: [],
    timelines: [],
  };

  initTextSequenceEffect(store);
  initSplitRevealEffect(store);
  ScrollTrigger.refresh();

  return () => {
    store.timelines.forEach((timeline) => timeline.kill());
    store.splitInstances.forEach((instance) => instance.revert());
    ScrollTrigger.refresh();
  };
}

let cleanupTextEffects: (() => void) | null = null;

function runWhenDomReady(fn: () => void) {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", fn, { once: true });
    return;
  }
  fn();
}

function runAfterFontsReady(fn: () => void) {
  const fonts = document.fonts;
  if (!fonts || typeof fonts.ready === "undefined") {
    fn();
    return;
  }

  fonts.ready.then(fn).catch(fn);
}

handleEditor((isEditor) => {
  if (isEditor) {
    cleanupTextEffects?.();
    cleanupTextEffects = null;
    runWhenDomReady(() => {
      showAllTextEffects();
    });
    return;
  }

  runWhenDomReady(() => {
    runAfterFontsReady(() => {
      cleanupTextEffects?.();
      cleanupTextEffects = initTextEffects();
    });
  });
});
